import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, AsyncSubject } from "rxjs";
import { AllCodeResponseHandler, MEAPIResponse, MEBasicAPIResponse, MEBasicAPIResponseHandler, MEDoNothingBasicResponseHandler, MEEntityAPIResponseHandler, ResponseCode } from "../api/me-api-response";
import { MEAPIService } from "../api/me-api.service";
import { InternalSettingsService } from "../internal-settings/internal-settings.service";
import { MEErrorService } from "../../error/me-error.service";
import { InternalSettings } from "../internal-settings/internal-settings.interface";
import { ExternalSettings } from "../external-settings/external-settings.interface";
import { LocalStorageService, LocalStorageType } from "src/app/system/utility/local-storage.service";
import type { BiometricService } from "src/app/system/utility/biometric.service";
import { LoginRequest, LoginStatus } from "src/app/business/login/login-bean";
import { LoginFormCallbackHost } from "src/app/business/login/login-form-callback.interface";
import { MEError } from "../../error/me-error";
import { PlatformService } from "src/app/system/utility/platform.service";
import { EnvironmentService } from "src/app/system/utility/environment.service";
import { environment } from 'src/environments/environment';
import { ResponsiveDesignService } from "../responsive-design/responsive-design.service";
import { NavigationService } from "src/app/system/utility/navigation.service";
import { CryptoService } from "src/app/system/utility/crypto.service";
import { UserSessionService } from "../user-session/user-session.service";
import { QrDeepLinkService } from "../qr-deep-link/qr-deep-link.service";

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private readonly API_PING: string = MEAPIService.BASE_URL + "/customer/ping";
  private readonly API_LOGIN: string = MEAPIService.BASE_URL + '/customer/login';
  private readonly API_NEXT_LOGIN_CONDITION: string = MEAPIService.BASE_URL + "/customer/getNextLoginCondition";

  public isLoggedIn$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    public router: Router,
    private readonly srvAPI: MEAPIService,
    private readonly srvUserSession: UserSessionService,
    private readonly srvIS: InternalSettingsService,
    private readonly srvRpDesign: ResponsiveDesignService,
    private readonly srvError: MEErrorService,
    private readonly srvQrDeepLink: QrDeepLinkService
  ) { }

  public doPing(preventRedirect: boolean = false): AsyncSubject<boolean> {

    if (!this.srvError.systemAvailable.value) {
      let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
      ret.next(false);
      ret.complete();
      return ret;
    }

    return this.srvAPI.BasicGet(this.API_PING, [
      new SuccessPingHandler(this),
      new MEDoNothingBasicResponseHandler(ResponseCode.SYSTEM_UNAVAILABLE),
      new NeedLoginHandler(this, this.srvIS, ResponseCode.USER_UNAUTHENTICATED, preventRedirect),
      new NeedLoginHandler(this, this.srvIS, ResponseCode.SESSION_TIMEOUT, preventRedirect),
      new NeedLoginHandler(this, this.srvIS, ResponseCode.USER_FORCED_CHANGE_PASSWORD, preventRedirect),
      new NeedLoginHandler(this, this.srvIS, ResponseCode.USER_PASSWORD_EXPIRED, preventRedirect),
      new NeedLoginHandler(this, this.srvIS, ResponseCode.ID_SCAN_VALIDATION, preventRedirect),
      new MEDoNothingBasicResponseHandler(ResponseCode.ALL_CODE_HANDLER)
    ]);
  }

  // Handle Shortcut to return currently login status
  public isLoggedIn(): boolean {
    return this.isLoggedIn$.value;
  }

  /**
   * Logout customer, house keep is triggerd in session service
   */
  public logoff(isSilent: boolean = false, redirectUrl?: string): AsyncSubject<boolean> {
    // If previouslly logged in, then reload the page
    let reload = this.isLoggedIn$.value;
    return this.srvUserSession.logoff(isSilent, reload, redirectUrl);
  }

  /**
   * Unconditionally logoff customer and hard return to login.
   */
  public forceLogoff(): void {
    this.srvUserSession.forceLogoff();
  }

  public logon(param: LoginRequest, externalSettings: ExternalSettings, parent: LoginFormCallbackHost): AsyncSubject<boolean> {

    // Redirect Traffic is match
    if (PlatformService.isMobile() && environment.production) {
      let reviewHash = externalSettings.globalVariables.mobiBankingReviewHash;
      let reviewUrl = externalSettings.globalVariables.mobiBankingReviewUrl;
      if (reviewHash && reviewUrl) {
        let hashcode = CryptoService.sha256(param.userId);
        if (hashcode == reviewHash) {
          EnvironmentService.redirectTraffic(reviewUrl);
        }
      }
    }

    let commonRedirectHandler = this.getCommonRedirectHandler();
    // Logoff Silently unconditionally before login
    return this.srvAPI.Post<LoginStatus>(this.API_LOGIN, param, [
      new SuccessfulLoginHandler(ResponseCode.SUCCESS, this, parent),
      new SuccessfulLoginHandler(ResponseCode.USER_ALREADY_LOGGED_IN, this, parent),
      new MFAValidationHandler(ResponseCode.MFA_VALIDATION, this, parent),
      new AppAuthMFAValidationHandler(ResponseCode.MFA_AUTH_APP, this, parent),
      new IDScanValidationHandler(ResponseCode.ID_SCAN_VALIDATION, this, parent),
      new AppAuthenticatorNeedsActivationHandler(ResponseCode.APP_AUTHENTICATOR_NEEDS_ACTIVATION, this, parent),
      ...commonRedirectHandler
    ]);
  }

  public handleLogout(isSilent: boolean, reload: boolean, redirectUrl?: string) {
    this.srvUserSession.finishLogout(isSilent, reload, redirectUrl);
  }

  public getNextCondition(url: string = '/dashboard'): AsyncSubject<boolean> {

    let redirectHandlers = this.getCommonRedirectHandler();
    return this.srvAPI.BasicGet(this.API_NEXT_LOGIN_CONDITION,
      [
        new MESuccessLoginHandler(ResponseCode.SUCCESS, this.srvQrDeepLink.resolvePostLoginUrl(url), this),
        ...redirectHandlers]);
  }

  public isSelfRegistrationApplicationsRequired(): AsyncSubject<boolean> {
    let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
    this.srvAPI.BasicGet(this.API_NEXT_LOGIN_CONDITION, [
      new AllCodeResponseHandler(ret, ResponseCode.SELF_REGISTRATION_APPLICATIONS_REQUIRED, true),
      new AllCodeResponseHandler(ret, ResponseCode.ALL_CODE_HANDLER, false)
    ]);
    return ret;
  }

  public getCommonRedirectHandler(): MEBasicAPIResponseHandler[] {
    return [
      new MEUrlResponseHandler(ResponseCode.USER_FORCED_CHANGE_PASSWORD, '/change-password/forced', this),
      new MEUrlResponseHandler(ResponseCode.USER_PASSWORD_EXPIRED, '/change-password/expired', this),
      new MEUrlResponseHandler(ResponseCode.TERMS_AND_CONDITIONS_REQUIRED, '/terms-and-conditions', this),
      new MEUrlResponseHandler(ResponseCode.SECURITY_QUESTION_REQUIRED, '/security-question', this),
      new MEUrlResponseHandler(ResponseCode.SELF_REGISTRATION_APPLICATIONS_REQUIRED, '/services/applications/self-registration', this),
      new MEUrlResponseHandler(ResponseCode.CIF_UPDATE, '/customer-information/update', this),
      new MEUrlResponseHandler(ResponseCode.FORCE_CIF_UPDATE, '/customer-information/forced', this),
      new MECorporatePaymentUserHandler(ResponseCode.CORPORATE_PAYMENT_USER, this, this.srvError, this.srvRpDesign), // Redirect to Corporate Payment and set Login
    ];
  }

  public static isCompanyCustomer(is: InternalSettings, es: ExternalSettings): boolean {
    return is.profile.isCompanyUser || is.profile.isCompanyAdmin;
  }

  public static isCorporateAgent(is: InternalSettings, es: ExternalSettings): boolean {
    let isCorporatePaymentEnable = es.globalVariables.isModuleEnabledCorporatePayment;
    return (isCorporatePaymentEnable && is.profile.isCorporateBillPayUser);
  }

  public static isBiometricSupport(srvLocalStorage: LocalStorageService, srvBiometric: BiometricService): boolean {
    if (!srvLocalStorage.has(LocalStorageType.REMEMBER_ME)) return false;
    return (srvBiometric.isPlatformSupportBiometricLogin()?.isPlatformSupport);
  }

  public static isPartialLogon(url: string): boolean {
    const partialLogons = [
      '/terms-and-conditions',
      '/change-password/',
      '/security-question',
      '/services/applications/self-registration',
      '/customer-information',
      '/id-scan'
    ];
    return partialLogons.find((x) => url.includes(x)) !== undefined;
  }

  public static isPreLogon(url: string): boolean {
    const preLogons = [
      '/login',
      '/forgot-password',
      '/self-registration/create',
      '/contact-us',
      '/mobile-location-map',
      '/license-viewer'
    ];
    return preLogons.some(x => url === x || url.startsWith(x + '?'));
  }

  public static isLoginPage(url: string): boolean {
    const logins = ['/login'];
    return logins.find((x) => url.includes(x)) !== undefined;
  }

  public setSession(response: MEAPIResponse<LoginStatus>) {
    if (PlatformService.isBrowser()) return;
    if (response.entity?.jsessionid) {
      sessionStorage.setItem('jsessionid', response.entity?.jsessionid);
    }
  }

  public getSession(): string {
    if (PlatformService.isBrowser()) return '';
    return sessionStorage.getItem('jsessionid') || '';
  }

  public handleInvalidUserStatus(code: ResponseCode, preventRedirect: boolean = false): boolean {
    switch (code) {
      case ResponseCode.SESSION_TIMEOUT:
      case ResponseCode.USER_UNAUTHENTICATED:
        if (!preventRedirect) {
          this.router.navigate(['/login']);
        }
        return true;
    }
    return false;
  }

}

class MECorporatePaymentUserHandler implements MEEntityAPIResponseHandler<LoginStatus> {
  constructor(public code: ResponseCode, private srvUser: UserService, private srvError: MEErrorService, private srvRpDesign: ResponsiveDesignService) { }
  handle(response: MEAPIResponse<LoginStatus>): boolean {

    if (this.srvRpDesign.isMobile$.value) {
      this.srvError.showDialog('LOGIN.CORPORATE_AGENT_MOBILE_NOT_SUPPORT_TITLE', 'LOGIN.CORPORATE_AGENT_MOBILE_NOT_SUPPORT_MESSAGE').subscribe(() => {
        this.srvUser.logoff();
      });
    }
    else {
      this.srvUser.setSession(response);
      this.srvUser.isLoggedIn$.next(true);
      NavigationService.gotoToCorporateLanding(this.srvUser.router);
    }
    return true;
  }
}

class MESuccessLoginHandler implements MEEntityAPIResponseHandler<LoginStatus> {
  constructor(public code: ResponseCode, private url: string, private srvUser: UserService) { }
  handle(response: MEAPIResponse<LoginStatus>): boolean {
    this.srvUser.setSession(response);
    this.srvUser.isLoggedIn$.next(true);
    NavigationService.goto(this.srvUser.router, this.url);
    return true;
  }
}

class MEUrlResponseHandler implements MEEntityAPIResponseHandler<LoginStatus> {
  
  constructor(public code: ResponseCode, private url: string, private srvUser: UserService) { }
  
  handle(response: MEAPIResponse<LoginStatus>): boolean {
    this.srvUser.setSession(response);
    NavigationService.goto(this.srvUser.router, this.url);
    return true;
  }
}

class SuccessfulLoginHandler implements MEEntityAPIResponseHandler<LoginStatus> {
  constructor(public code: ResponseCode, private srvUser: UserService, private loginFormHost: LoginFormCallbackHost) { }
  handle(response: MEAPIResponse<LoginStatus>): boolean | MEError {
    this.srvUser.setSession(response);
    return this.loginFormHost.successfulLogin();
  }
}

class MFAValidationHandler implements MEEntityAPIResponseHandler<LoginStatus>{
  constructor(public code: ResponseCode, private srvUser: UserService, private loginFormHost: LoginFormCallbackHost) { }
  handle(response: MEAPIResponse<LoginStatus>): boolean | MEError {
    this.srvUser.setSession(response);
    return this.loginFormHost.showMFA(false);
  }
}

class AppAuthMFAValidationHandler implements MEEntityAPIResponseHandler<LoginStatus>{
  constructor(public code: ResponseCode, private srvUser: UserService, private loginFormHost: LoginFormCallbackHost) { }
  handle(response: MEAPIResponse<LoginStatus>): boolean | MEError {
    this.srvUser.setSession(response);
    return this.loginFormHost.showMFA(true);
  }
}

class AppAuthenticatorNeedsActivationHandler implements MEEntityAPIResponseHandler<LoginStatus>{
  constructor(public code: ResponseCode, private srvUser: UserService, private loginFormHost: LoginFormCallbackHost) { }
  handle(response: MEAPIResponse<LoginStatus>): boolean | MEError {
    this.srvUser.setSession(response);
    return this.loginFormHost.showActivateAppAuthenticator();
  }
}

class IDScanValidationHandler implements MEEntityAPIResponseHandler<LoginStatus>{
  constructor(public code: ResponseCode, private srvUser: UserService, private loginFormHost: LoginFormCallbackHost) { }
  handle(response: MEAPIResponse<LoginStatus>): boolean | MEError {
    this.srvUser.setSession(response);
    return this.loginFormHost.showIDScan();
  }
}

/**
 * For Logoff Response Handling
 */
class SuccessLogoffHandler implements MEBasicAPIResponseHandler {
  public readonly code: ResponseCode = ResponseCode.SUCCESS;

  constructor(private srvUser: UserService, private isSilent: boolean, private reload: boolean, private redirectUrl?: string) { }
  handle(_response: MEBasicAPIResponse): boolean {
    this.srvUser.handleLogout(this.isSilent, this.reload, this.redirectUrl);
    return true;
  }
}

/**
 * For Ping Response Handling
 */
class SuccessPingHandler implements MEBasicAPIResponseHandler {
  public readonly code: ResponseCode = ResponseCode.SUCCESS;

  constructor(private srvUser: UserService) { }
  handle(_response: MEBasicAPIResponse): boolean {

    let currentValue = this.srvUser.isLoggedIn$.value;
    if (currentValue == false) { // Only togger to true if not match
      this.srvUser.isLoggedIn$.next(true);
    }

    return true;
  }
}

class NeedLoginHandler implements MEBasicAPIResponseHandler {
  constructor(private srvUser: UserService, private srvIS: InternalSettingsService, public readonly code: ResponseCode, private preventRedirect: boolean = false) { }
  handle(_response: MEBasicAPIResponse): boolean {

    let currentValue = this.srvUser.isLoggedIn$.value;
    if (currentValue == true) { // Only togger to true if not match
      this.srvUser.isLoggedIn$.next(false);
    }

    let internalSettings = this.srvIS.internalSettings$.value;
    if (internalSettings) { // Only set to Null if not null
      this.srvIS.internalSettings$.next(null);
    }

    this.srvUser.handleInvalidUserStatus(this.code, this.preventRedirect);

    return true;
  }
}